/**
 * Dynamic Customer View Flow
 *
 * This component supports dynamic ticket codes via URL parameters:
 *
 * DEMO Mode: http://localhost:3000/getticket/DEMO
 * - Generates random queue numbers (1-99)
 * - No database entries created
 * - Shows "DEMO MODE" indicators
 *
 * LIVE Mode: http://localhost:3000/getticket/4H9PP3
 * - Creates actual queue entries in database
 * - Uses real client services
 * - Shows "LIVE MODE" indicators
 *
 * Default: If no ticket parameter is provided, defaults to DEMO mode
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import {
    Box,
    Typography,
    Button,
    useTheme,
    useMediaQuery,
    Link as MuiLink,
    CircularProgress,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import BranchHeader from "./BranchHeader";
import {
    getAllServices,
    getTicket,
    createQueueWithServices,
    getServiceSettings,
    getBranchDisplayInfo,
} from "./../../../api/api"; // Centralized API import
import { getStorageUrl } from "../../../config/config";
import ScrollButtons from "../../../components/ScrollButtons";
import QueueManager from "../../../utils/QueueManager";
import { getCurrentBranchId } from "../../../utils/auth";

const SelectionPage = () => {
    // Dynamic ticket mode: Uses URL parameter 'ticket' to determine if demo or real mode
    // Example: /getticket/DEMO (demo mode) or /getticket/4H9PP3 (real mode)
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [queueNumber, setQueueNumber] = useState(null);
    const [isDemoMode, setIsDemoMode] = useState(false);
    const [serviceSelectionText, setServiceSelectionText] = useState("We only have one queue, but if we know what you need help with we can assist you better. You can choose multiple services.");
    const [nextButtonText, setNextButtonText] = useState("Neste");
    const [branchId, setBranchId] = useState(null);
    const [branchName, setBranchName] = useState("");
    const [branchLogoUrl, setBranchLogoUrl] = useState(null);
    const navigate = useNavigate();
    const { code } = useParams();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    // Get ticket code from URL parameter
    const ticketCode = code || "DEMO"; // Default to DEMO if no parameter

    // Log the current mode for debugging
    console.log(
        `🎫 Customer View Mode: ${
            ticketCode === "DEMO" ? "DEMO" : "LIVE"
        } (Code: ${ticketCode})`
    );

    // Load dynamic text settings when branchId is available
    useEffect(() => {
        const fetchTextSettings = async () => {
            if (!branchId) return;
            try {
                const response = await getServiceSettings(branchId);
                if (response.success && response.data) {
                    if (response.data.service_selection_text) {
                        setServiceSelectionText(response.data.service_selection_text);
                    }
                    if (response.data.next_button_text) {
                        setNextButtonText(response.data.next_button_text);
                    }
                }
            } catch (err) {
                console.error("Error fetching service text settings:", err);
            }
        };
        fetchTextSettings();
    }, [branchId]);

    // Branch branding. Hardcoding one shop's logo here showed every other
    // client that shop's brand, so read it from the branch on the ticket.
    useEffect(() => {
        if (!branchId) return;
        let cancelled = false;
        const fetchBranding = async () => {
            const data = await getBranchDisplayInfo(branchId);
            if (cancelled || !data) return;
            setBranchName(data.branch_name || "");
            setBranchLogoUrl(
                data.logo ? getStorageUrl(`/storage/logos/${data.logo}`) : null
            );
        };
        fetchBranding();
        return () => {
            cancelled = true;
        };
    }, [branchId]);

    useEffect(() => {
        const fetchOptionsAndQueue = async () => {
            try {
                // Determine if we're in demo mode
                const demoMode = ticketCode === "DEMO";
                setIsDemoMode(demoMode);

                // Check for existing queue data first (localStorage)
                const existingQueueData = QueueManager.getQueue(ticketCode);
                if (existingQueueData && !demoMode) {
                    console.log(
                        "Existing queue data found in localStorage:",
                        existingQueueData
                    );

                    // Set queue number from stored data
                    setQueueNumber(existingQueueData.queueNumber);

                    // If services were already selected, redirect to NextCustomerView
                    if (
                        existingQueueData.selectedOptions &&
                        existingQueueData.selectedOptions.length > 0
                    ) {
                        console.log(
                            "Services already selected, redirecting to NextCustomerView"
                        );
                        navigate("/home/Next_customer-view", {
                            state: {
                                selectedOptions:
                                    existingQueueData.selectedOptions,
                                queueNumber: existingQueueData.queueNumber,
                                ticketCode,
                                isDemoMode,
                                existing: true,
                            },
                        });
                        return;
                    } else {
                        // Services not selected yet, show service selection page
                        console.log(
                            "Services not selected yet, showing service selection page"
                        );

                        // Set the selected options from localStorage if they exist
                        if (existingQueueData.selectedOptions) {
                            setSelectedOptions(
                                existingQueueData.selectedOptions
                            );
                        }

                        // Continue to load services for selection
                    }
                }

                // Generate queue number based on ticket code
                const res = await getTicket(ticketCode);
                console.log("res", res);
                setQueueNumber(res.queue_number);

                // Store initial queue data in localStorage
                QueueManager.setQueue(ticketCode, {
                    queueNumber: res.queue_number,
                    selectedOptions: [],
                    existing: res.existing || false,
                });

                // Check if this is an existing queue from database
                if (res.existing) {
                    console.log(
                        "Existing queue found in database, but still showing service selection page"
                    );
                    // Don't redirect - let user select services even for existing queue
                    // The queue will be updated with services when they click Next
                }

                // Get services using the branch ID from the ticket response
                const resolvedBranchId = res.client?.id || getCurrentBranchId();
                setBranchId(resolvedBranchId);
                const data = await getAllServices(resolvedBranchId);
                console.log("Services loaded for selection:", data);
                setOptions(data);
            } catch (err) {
                console.error("Failed to load data", err);
                setError("Kunne ikke laste alternativer.");
            } finally {
                setLoading(false);
            }
        };

        fetchOptionsAndQueue();
    }, [ticketCode, navigate]);

    const handleSelect = (option) => {
        setSelectedOptions((prevSelected) => {
            // Check if option is already selected (by service_name for objects, or by value for strings)
            const isSelected = prevSelected.some((item) => {
                if (typeof option === "object" && option.service_name) {
                    return item.service_name === option.service_name;
                }
                return item === option;
            });

            if (isSelected) {
                // Remove the option
                return prevSelected.filter((item) => {
                    if (typeof option === "object" && option.service_name) {
                        return item.service_name !== option.service_name;
                    }
                    return item !== option;
                });
            } else {
                // Add the option
                return [...prevSelected, option];
            }
        });
    };

    const handleNext = async () => {
        // Log the selected options for debugging
        console.log("Selected Options with service_tags:", selectedOptions);

        try {
            if (!isDemoMode && ticketCode !== "DEMO") {
                // Create queue with selected services for live mode
                const serviceNames = selectedOptions
                    .map((option) =>
                        typeof option === "object"
                            ? option.service_name
                            : option
                    )
                    .join(", ");

                console.log("Creating queue with services:", serviceNames);

                // Get current time from UI to ensure consistency
                const currentUITime = new Date().toISOString();
                const queueResponse = await createQueueWithServices(
                    ticketCode,
                    serviceNames,
                    currentUITime
                );
                console.log("Queue created:", queueResponse);

                // Update queue number with the actual created queue number
                setQueueNumber(queueResponse.queue_number);

                // Store updated queue data with services in localStorage
                QueueManager.updateQueue(ticketCode, {
                    queueNumber: queueResponse.queue_number,
                    selectedOptions: selectedOptions,
                    serviceNames: serviceNames,
                });
            }

            // Pass the ticket code and demo mode state to the next page
            navigate("/home/Next_customer-view", {
                state: { selectedOptions, queueNumber, ticketCode, isDemoMode },
            });
        } catch (error) {
            console.error("Error creating queue:", error);
            setError("Kunne ikke opprette kø. Prøv igjen.");
        }
    };

    return (
        <>
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                px={2}
                sx={{ mt: { xs: 6, sm: 10 } }}
            >
                <BranchHeader
                    logoUrl={branchLogoUrl}
                    branchName={branchName}
                    isMobile={isMobile}
                />

                <Typography
                    variant="subtitle1"
                    color="text.secondary"
                    textAlign="center"
                    mb={3}
                    sx={{ maxWidth: 500 }}
                >
                    {isDemoMode ? (
                        <strong style={{ color: "#1976d2" }}>DEMO MODE</strong>
                    ) : (
                        ""
                    )}{" "}
 {serviceSelectionText}
                </Typography>

                {loading ? (
                    <CircularProgress />
                ) : error ? (
                    <Typography color="error">{error}</Typography>
                ) : (
                    <Box sx={{ width: "100%", maxWidth: 400 }}>
                        {options.map((option, index) => (
                            <Button
                                key={index}
                                variant={
                                    selectedOptions.some((item) => {
                                        if (
                                            typeof option === "object" &&
                                            option.service_name
                                        ) {
                                            return (
                                                item.service_name ===
                                                option.service_name
                                            );
                                        }
                                        return item === option;
                                    })
                                        ? "contained"
                                        : "outlined"
                                }
                                fullWidth
                                sx={{ mb: 1, textTransform: "none" }}
                                onClick={() => handleSelect(option)}
                            >
                                {option.service_name || option}
                            </Button>
                        ))}
                    </Box>
                )}

                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleNext}
                    sx={{ mt: 2 }}
                    fullWidth={isMobile}
                    disabled={selectedOptions.length === 0 || !queueNumber}
                >
                    {nextButtonText || "Neste"}
                </Button>

                <Box mt={4} textAlign="center">
                    <Typography variant="body2">
                        <MuiLink href="#" underline="hover">
                            easyQue
                        </MuiLink>{" "}
                        (c) by Digifront
                    </Typography>
                    <Typography variant="body2" mt={1}>
                        <MuiLink
                            href="mailto:sales@digifront.biz"
                            underline="hover"
                        >
                            sales@digifront.biz
                        </MuiLink>
                    </Typography>
                </Box>
            </Box>
            <ScrollButtons />
        </>
    );
};

export default SelectionPage;
